import {useState} from 'react';
import {useI18n} from '@shopify/react-i18n';
import {
  Button,
  Checkbox,
  Modal,
  TextContainer,
  TextField,
} from '@shopify/polaris';

interface Props {
  open: boolean;
  link: {
    url?: string;
    title?: string;
    target?: string;
    text?: string;
    hasLink?: boolean;
  };
  onClose(): void;
  handleUpsert(modalData: LinkEditModalValue): void;
  handleRemove(): void;
}

export interface LinkEditModalValue {
  url?: string;
  title?: string;
  targetBlank: boolean;
  userInputText?: string;
  initialText?: string;
}

export const LinkEditModal = ({
  open,
  link,
  onClose,
  handleUpsert,
  handleRemove,
}: Props) => {
  const [i18n] = useI18n();

  const {
    url: linkUrl,
    title: linkTitle,
    target: linkTarget,
    text: initialText,
    hasLink,
  } = link;
  const [userInputText, setUserInputText] = useState(initialText);
  const [url, setUrl] = useState(linkUrl);
  const [title, setTitle] = useState(linkTitle);
  const [targetBlank, setTargetBlank] = useState(linkTarget === '_blank');

  const entryForm = (
    <TextContainer>
      <TextField
        autoComplete="off"
        label={i18n.translate('text')}
        value={userInputText ?? ''}
        onChange={setUserInputText}
      />
      <TextField
        autoComplete="off"
        label={i18n.translate('link')}
        helpText={i18n.translate('linkHelp')}
        value={url}
        onChange={setUrl}
      />
      {url && (
        <TextField
          autoComplete="off"
          label={i18n.translate('linkTitle')}
          helpText={i18n.translate('linkTitleHelp')}
          value={title}
          onChange={setTitle}
        />
      )}
      <Checkbox
        label={i18n.translate('openLinkInNewWindow')}
        checked={targetBlank}
        onChange={setTargetBlank}
      />
    </TextContainer>
  );

  const disabled =
    !userInputText?.length ||
    !url?.length ||
    (url === linkUrl &&
      title === linkTitle &&
      userInputText === initialText &&
      targetBlank === (linkTarget === '_blank'));

  return (
    <Modal
      title={
        hasLink ? i18n.translate('editLink') : i18n.translate('insertLink')
      }
      open={open}
      primaryAction={{
        disabled,
        content: hasLink
          ? i18n.translate('editLink')
          : i18n.translate('insertLink'),
        onAction: () =>
          handleUpsert({url, title, targetBlank, userInputText, initialText}),
      }}
      secondaryActions={[
        {
          content: i18n.translate('cancel'),
          onAction: onClose,
        },
      ]}
      footer={
        hasLink && (
          <Button tone="critical" onClick={handleRemove}>
            {i18n.translate('removeLink')}
          </Button>
        )
      }
      onClose={onClose}
    >
      <Modal.Section>{entryForm}</Modal.Section>
    </Modal>
  );
};
